// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { BillToModel, CarrierDto, ShipToModel, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import BillingAddressInfoDisplay, {
    BillingAddressInfoDisplayStyles,
} from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/BillingAddressInfoDisplay";
import { ShippingAddressInfoDisplayStyles } from "@insite/content-library/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { checkoutSharedStyles } from "@tps/App/SharedStyles/CheckoutSharedStyles";
import { CustomAppState } from "@tps/App/Store/Reducers";
import AddressInfoDisplay from "@tps/Overrides/Components/AddressInfoDisplay";
import { WarehousesObj } from "@tps/Overrides/Widgets/CheckoutReviewAndSubmit/CheckoutReviewAndSubmitShippingInfo";
import ShippingAddressInfoDisplay from "@tps/Overrides/Widgets/CheckoutReviewAndSubmit/ShippingAddressInfoDisplay";
import xScreen from "@tps/XDL/Utilities/xScreen";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    billTo: BillToModel;
    shipTo: ShipToModel;
    carrier: CarrierDto;
    shipVia: ShipViaDto;
    deliveryDate: Date | null;
    onEditBillTo: () => void;
    onEditShipTo: () => void;
    extendedStyles?: BillingAndShippingAddressStyles;
    hasMultipleWarehouses?: boolean;
    hasWillCall?: boolean;
    isSingleWarehouseWithWillCall?: boolean;
    firstWarehouse?: { [key: string]: string };
}

const mapStateToProps = (state: CustomAppState) => ({
    requiresMtrFromStore: state.xCheckout.requiresMtr,
    allowBillToEdit: getSettingsCollection(state).customerSettings.allowBillToAddressEdit,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface BillingAndShippingAddressStyles {
    container?: GridContainerProps;
    carrierGridItem?: GridItemProps;
    carrierHeadingText?: TypographyPresentationProps;
    carrierText?: TypographyPresentationProps;
    shippingServiceGridItem?: GridItemProps;
    shippingServiceHeadingText?: TypographyPresentationProps;
    shippingServiceText?: TypographyPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDateHeadingText?: TypographyPresentationProps;
    deliveryDateText?: TypographyPresentationProps;
    shippingAddressGridItem?: GridItemProps;
    shippingAddress?: ShippingAddressInfoDisplayStyles;
    billingAddressGridItem?: GridItemProps;
    billingAddress?: BillingAddressInfoDisplayStyles;
    // xng-change
    wrapper?: InjectableCss;
    billinigAndOrderDetails?: InjectableCss;
    shippingAddressSectionWrapper?: InjectableCss;
    pickupAddressGridItem?: GridItemProps;
    pickupAddressWrapper?: InjectableCss;
    pickupAddressInfoWrapper?: InjectableCss;
    pickupAddressInfoGridItem?: GridItemProps;
    pickupWarehousesText?: TypographyProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    pickupAddressContainer?: GridContainerProps;
}

export const billingAndShippingAddressStyles: BillingAndShippingAddressStyles = {
    container: { gap: 20 },
    carrierGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    shippingServiceHeadingText: { weight: 600 },
    shippingServiceGridItem: {
        width: [6, 6, 3, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    carrierHeadingText: { weight: 600 },
    deliveryDateGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryDateHeadingText: { weight: 600 },
    shippingAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    billingAddressGridItem: {
        width: [12, 12, 6, 6, 6],
    },
};

// XNG-Change: remove service, carrier and requested delivery date sections as they are not included in FRD or design
const BillingAndShippingInfo = ({
    billTo,
    shipTo,
    carrier,
    shipVia,
    deliveryDate,
    onEditBillTo,
    onEditShipTo,
    extendedStyles,
    // XNG-Change
    requiresMtrFromStore,
    allowBillToEdit,
    hasMultipleWarehouses,
    hasWillCall,
    isSingleWarehouseWithWillCall,
    firstWarehouse,
}: Props) => {
    const styles = mergeToNew(billingAndShippingAddressStyles, extendedStyles);
    const [requiresMtr, setRequiresMtr] = useState(requiresMtrFromStore);
    const dispatch = useDispatch();

    const requiresMtrChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setRequiresMtr(value);
        dispatch({ type: "xCheckout/SetRequiresMtr", requiresMtr: value });
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.billingAddressGridItem}>
                <StyledWrapper {...styles.billinigAndOrderDetails}>
                    <BillingAddressInfoDisplay
                        billTo={billTo}
                        onEdit={onEditBillTo}
                        extendedStyles={styles.billingAddress}
                        {...{ canBeEdited: allowBillToEdit }}
                    />
                    {/* XNG-Change: Add Order details section */}
                    <StyledWrapper {...checkoutSharedStyles.billingInfoAndOrderDetailsSeparator} />
                    <StyledWrapper {...styles.wrapper}>
                        <Typography {...checkoutSharedStyles.subSectionTitle}>{translate("Order Details")}</Typography>
                        <CheckboxGroup>
                            <Checkbox checked={requiresMtr} onChange={requiresMtrChangeHandler}>
                                {translate("This Order Requires MTR")}
                            </Checkbox>
                        </CheckboxGroup>
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            {/* XNG-Change: */}
            {(!hasWillCall || (hasMultipleWarehouses && hasWillCall)) && (
                <GridItem {...styles.shippingAddressGridItem}>
                    <StyledWrapper {...styles.shippingAddressSectionWrapper}>
                        <ShippingAddressInfoDisplay
                            shipTo={shipTo}
                            onEdit={onEditShipTo}
                            extendedStyles={styles.shippingAddress}
                            // XNG-Change
                            hasMultipleWarehouses={hasMultipleWarehouses}
                            hasWillCall={hasWillCall}
                        />
                    </StyledWrapper>
                </GridItem>
            )}
            {/* XNG-Change: */}
            {isSingleWarehouseWithWillCall && firstWarehouse && (
                <GridItem {...styles.pickupAddressGridItem}>
                    <Typography {...styles.headingText}>{translate("Pickup Address")}</Typography>
                    <AddressInfoDisplay
                        companyName={firstWarehouse.Description || firstWarehouse.Name}
                        address1={firstWarehouse.Address1}
                        address2={firstWarehouse.Address2}
                        city={firstWarehouse.City}
                        state={firstWarehouse.State ?? undefined}
                        postalCode={firstWarehouse.PostalCode}
                        country={firstWarehouse.Country ?? undefined}
                        phone={firstWarehouse.Phone}
                    />
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(mapStateToProps)(BillingAndShippingInfo);
